import React, { type FC, useEffect } from 'react'
import { Box, CircularProgress, type SxProps, type Theme, Typography } from '@mui/material'

import { isArray, type ReadonlyRecord } from '../../../utils'

import { ConnectwareError, selectSupportedSSOs, SSOAuthenticationMethod, Translation } from '../../../domain'

import { useAsyncCallback } from '../Callback'
import { Button, MSIcon } from '../common'
import { FormattedTranslation } from '../Internationalization'
import { useAppState, useAppUsecase } from '../State'
import { LoginValidationError } from './Common'

const useEntraIdSectionStyle: SxProps<Theme> = { my: 4 }
const useUserNameStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%', position: 'relative', color: 'white', mt: 2 }
const useUserNameLabelStyle: SxProps<Theme> = { mx: 4, textTransform: 'uppercase' }
const useUserNameLineStyle: SxProps<Theme> = { flexGrow: 1, height: '1px', backgroundColor: '#ffffff3b' }
const useSignInButtonStyle: SxProps<Theme> = { py: 2, mb: 2 }
const useLoadingIndicatorStyle: SxProps<Theme> = { display: 'flex', justifyContent: 'center', width: '100%' }
const useErrorStyle: SxProps<Theme> = { my: 4 }

const ssoIconMap: ReadonlyRecord<SSOAuthenticationMethod, FC> = {
    [SSOAuthenticationMethod.MICROSOFT_ENTRA_ID]: MSIcon,
}

export const LoginWithSSO: FC = () => {
    const usecase = useAppUsecase('ssoLoginUsecase')
    const supportedSSOTypes = useAppState(selectSupportedSSOs)

    useEffect(() => void usecase.fetchSupported(), [usecase])

    const [redirect, isRedirecting, result] = useAsyncCallback((type: SSOAuthenticationMethod) => usecase.redirectToAuthenticationUrl(type), [usecase])

    const isLoading = supportedSSOTypes === null
    const hasError = ConnectwareError.is(supportedSSOTypes)

    return (
        <>
            {isLoading && (
                <Box sx={useLoadingIndicatorStyle}>
                    <CircularProgress data-testid="sso-circular-loading" size={20} color="info" />
                </Box>
            )}

            {hasError && (
                <LoginValidationError data-testid="sso-error-message">
                    <FormattedTranslation id={Translation.SSO_STATUS_FETCHING_ERROR} />
                </LoginValidationError>
            )}

            {isArray(supportedSSOTypes) &&
                supportedSSOTypes.map((method) => {
                    const Icon = ssoIconMap[method]

                    return (
                        <Box key={method} data-testid={`${method}-section`} sx={useEntraIdSectionStyle}>
                            <Button
                                data-testid={`sign-in-with-${method}-button`}
                                sx={useSignInButtonStyle}
                                startIcon={<Icon />}
                                onClick={() => redirect(method)}
                                variant="secondary"
                                size="large"
                                disabled={isRedirecting}
                                fullWidth
                            >
                                <FormattedTranslation id={Translation.SIGN_IN_WITH_SSO} values={{ method }} />
                            </Button>

                            {!isRedirecting && ConnectwareError.is(result) && (
                                <LoginValidationError sx={useErrorStyle} data-testid="redirect-to-sso-error-message">
                                    <FormattedTranslation id={Translation.SSO_REDIRECTING_ERROR} values={{ resultErrorType: result.type, method }} />
                                </LoginValidationError>
                            )}

                            <Box sx={useUserNameStyle}>
                                <Box sx={useUserNameLineStyle} />
                                <Typography sx={useUserNameLabelStyle} variant="body2">
                                    <FormattedTranslation id={Translation.OR_USE_USERNAME} />
                                </Typography>
                                <Box sx={useUserNameLineStyle} />
                            </Box>
                        </Box>
                    )
                })}
        </>
    )
}
